import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import LiveBadge from '@/components/LiveBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Play, StopCircle, Loader2, ExternalLink, Eye } from 'lucide-react';

const LiveStreamManagementPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [currentLive, setCurrentLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    video_title: '',
    youtube_link: '',
    notes: ''
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [reservationsRes, liveRes] = await Promise.all([
        api.get('/admin/live-streaming/reservations'),
        api.get('/admin/live-streaming/current')
      ]);
      setReservations(reservationsRes.data.reservations || []);
      setCurrentLive(liveRes.data.current_live);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleStartLive = async (reservation) => {
    if (!window.confirm(`Démarrer le LIVE pour "${reservation.video_title}" ?`)) return;
    
    try {
      await api.post(`/admin/live-streaming/${reservation.reservation_id}/start`);
      toast.success('✅ LIVE démarré !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de démarrage');
    }
  };

  const handleStopLive = async () => {
    if (!window.confirm('Arrêter le LIVE en cours ?')) return;
    
    try {
      await api.post(`/admin/live-streaming/${currentLive.reservation_id}/stop`);
      toast.success('✅ LIVE arrêté');
      fetchData();
    } catch (error) {
      toast.error('Erreur d\'arrêt du LIVE');
    }
  };

  const handleCreateTestReservation = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('video_title', testForm.video_title);
      formData.append('youtube_link', testForm.youtube_link);
      formData.append('notes', testForm.notes);
      
      await api.post('/admin/live-streaming/create-test', formData);
      toast.success('✅ Réservation TEST créée !');
      setShowTestModal(false);
      setTestForm({ video_title: '', youtube_link: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur de création');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            📺 Gestion des LIVE Streams
          </h1>
          <p className="text-zinc-400">
            Gérez les diffusions en direct des clips
          </p>
        </div>

        {/* Bouton Créer Réservation TEST */}
        <div className="mb-6">
          <Button
            onClick={() => setShowTestModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            ➕ Créer une Réservation LIVE (TEST)
          </Button>
        </div>

        {/* LIVE En Cours */}
        {currentLive && (
          <Card className="glass border-red-600 mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <LiveBadge size="large" />
                  LIVE EN COURS
                </CardTitle>
                <Button
                  variant="destructive"
                  onClick={handleStopLive}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <StopCircle size={18} className="mr-2" />
                  Arrêter le LIVE
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-white">{currentLive.video_title}</h3>
                <div className="flex items-center gap-4 text-zinc-400">
                  <span>👤 {currentLive.user_name}</span>
                  <span>👥 {currentLive.viewers_count || 0} spectateurs</span>
                  <span>⏱️ Démarré: {new Date(currentLive.started_at).toLocaleString('fr-FR')}</span>
                </div>
                {currentLive.youtube_link && (
                  <a
                    href={currentLive.youtube_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
                  >
                    <ExternalLink size={16} />
                    Voir sur YouTube
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Réservations En Attente */}
        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle>Réservations En Attente ({reservations.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {reservations.length === 0 ? (
              <p className="text-center text-zinc-400 py-8">
                Aucune réservation en attente
              </p>
            ) : (
              <div className="space-y-4">
                {reservations.map((res) => (
                  <div
                    key={res.reservation_id}
                    className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg"
                  >
                    <div className="flex-1">
                      <h3 className="text-white font-bold">{res.video_title}</h3>
                      <div className="flex items-center gap-4 text-sm text-zinc-400 mt-1">
                        <span>👤 {res.user_name}</span>
                        <span>💰 {res.amount_usd}$</span>
                        <span>📅 {new Date(res.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {res.youtube_link && (
                        <a
                          href={res.youtube_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
                        >
                          <ExternalLink size={12} />
                          Prévisualiser
                        </a>
                      )}
                      {res.notes && (
                        <p className="text-xs text-zinc-500 mt-1">Note: {res.notes}</p>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => handleStartLive(res)}
                      disabled={!!currentLive}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Play size={18} className="mr-2" />
                      Démarrer LIVE
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LiveStreamManagementPage;
